import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { labApi } from './labClient';
import type {
  ConfirmLabDocumentInput,
  CreateLabOrderInput,
  LabRejectionReason,
  LabReportsQuery,
  LabWorklistQuery,
  UpdateLabOrderInput,
} from '@/types/lab';
import type { SpecimenType } from '@synapse/lab-tests';

export const LAB_KEY = ['lab'] as const;

/**
 * Every lab mutation returns the whole order, so the cache is seeded from the
 * response rather than invalidated-and-refetched. That matters more here than
 * elsewhere: an order's status is DERIVED from its tests and specimens, so a
 * partial update would leave the badge disagreeing with the rows under it until
 * a refetch landed.
 *
 * The lists are still invalidated — a status change moves an order between
 * worklist segments, and that cannot be patched from one order's response.
 */
/**
 * Seed the order in cache and invalidate everything derived from it.
 *
 * One function rather than a copy per mutation: the upload path cannot use
 * `useOrderMutation` (it needs progress and abort), so without this the same
 * four lines exist twice and a fifth key added later lands in one of them.
 */
function seedOrderCaches(
  qc: ReturnType<typeof useQueryClient>,
  order: { id: string; patientId: string },
) {
  qc.setQueryData([...LAB_KEY, 'order', order.id], order);
  void qc.invalidateQueries({ queryKey: [...LAB_KEY, 'worklist'] });
  void qc.invalidateQueries({ queryKey: [...LAB_KEY, 'reports'] });
  void qc.invalidateQueries({ queryKey: [...LAB_KEY, 'patient', order.patientId] });
  // The catalogue carries `pastFreeTextNames`, which the server derives from the
  // typed test names on past orders — so an order write can change it. Without
  // this, raising an order with a typed test and immediately raising another
  // leaves the name missing from the autosuggest for the whole 5-minute
  // staleTime, and the operator retypes it. Any spelling drift then slugs to a
  // different code and becomes a different test.
  void qc.invalidateQueries({ queryKey: [...LAB_KEY, 'catalogue'] });
}

function useOrderMutation<TArgs>(
  mutationFn: (args: TArgs) => ReturnType<typeof labApi.get>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (order) => seedOrderCaches(qc, order),
  });
}

/**
 * The effective test catalogue. Long `staleTime`: a clinic's test list changes
 * about as often as its letterhead, and the new-order form reads it on every
 * open.
 */
export function useLabCatalogue(opts: { enabled?: boolean; includeInactive?: boolean } = {}) {
  const includeInactive = opts.includeInactive ?? false;
  return useQuery({
    // The flag is part of the key: the management screen's list is a superset
    // of the ordering list, and sharing one cache entry would let whichever
    // screen loaded first decide what the other sees.
    queryKey: [...LAB_KEY, 'catalogue', { includeInactive }],
    queryFn: () => labApi.catalogue({ includeInactive }),
    enabled: opts.enabled ?? true,
    staleTime: 5 * 60_000,
  });
}

/**
 * The clinic's laboratories. `includeInactive` is in the key for the same reason
 * the catalogue's is: the worklist filter needs the superset and the ordering
 * picker must not see it.
 */
export function useLabLaboratories(
  opts: { enabled?: boolean; includeInactive?: boolean } = {},
) {
  const includeInactive = opts.includeInactive ?? false;
  return useQuery({
    queryKey: [...LAB_KEY, 'laboratories', { includeInactive }],
    queryFn: () => labApi.laboratories({ includeInactive }),
    enabled: opts.enabled ?? true,
    staleTime: 5 * 60_000,
  });
}

export function useLabWorklist(query: LabWorklistQuery, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...LAB_KEY, 'worklist', query],
    queryFn: () => labApi.worklist(query),
    enabled: opts.enabled ?? true,
    // Typing in the search box or stepping through pages should not flash the
    // empty state between renders.
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

export function useLabOrder(orderId: string | null | undefined, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...LAB_KEY, 'order', orderId],
    queryFn: () => labApi.get(orderId as string),
    enabled: (opts.enabled ?? true) && !!orderId,
    staleTime: 10_000,
  });
}

export function useLabReports(query: LabReportsQuery, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...LAB_KEY, 'reports', query],
    queryFn: () => labApi.reports(query),
    enabled: opts.enabled ?? true,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

export function usePatientLabOrders(
  patientId: string | null | undefined,
  opts: { limit?: number; enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [...LAB_KEY, 'patient', patientId, { limit: opts.limit }],
    queryFn: () => labApi.ordersForPatient(patientId as string, opts.limit),
    enabled: (opts.enabled ?? true) && !!patientId,
    staleTime: 30_000,
  });
}

export function useCreateLabOrder() {
  return useOrderMutation((input: CreateLabOrderInput) => labApi.create(input));
}

export function useUpdateLabOrder() {
  return useOrderMutation((args: { orderId: string; input: UpdateLabOrderInput }) =>
    labApi.update(args.orderId, args.input),
  );
}

export function useCancelLabOrder() {
  return useOrderMutation((args: { orderId: string; reason: string }) =>
    labApi.cancel(args.orderId, args.reason),
  );
}

export function useCancelLabTest() {
  return useOrderMutation((args: { orderId: string; testId: string; reason: string }) =>
    labApi.cancelTest(args.orderId, args.testId, args.reason),
  );
}

export function useCollectSpecimen() {
  return useOrderMutation(
    (args: {
      orderId: string;
      specimenType: SpecimenType;
      container?: string;
      collectedAt: string;
      labelNote?: string;
    }) => {
      const { orderId, ...input } = args;
      return labApi.collectSpecimen(orderId, input);
    },
  );
}

export function useRejectSpecimen() {
  return useOrderMutation(
    (args: { specimenId: string; reason: LabRejectionReason; note?: string }) => {
      const { specimenId, ...input } = args;
      return labApi.rejectSpecimen(specimenId, input);
    },
  );
}

export function useDeleteLabReport() {
  return useOrderMutation((args: { documentId: string }) => labApi.deleteReport(args.documentId));
}

/**
 * Upload a report.
 *
 * Not built on `useOrderMutation` because it needs progress and abort, and
 * because its success payload carries the duplicate warning alongside the order.
 * Deliberately no optimistic update — see `labApi.uploadReport`.
 */
export function useUploadLabReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      orderId: string;
      testId: string;
      file: File;
      supersedesDocumentId: string | null;
      reportDate?: string;
      labName?: string;
      onProgress?: (p: { loaded: number; total: number }) => void;
      signal?: AbortSignal;
    }) => {
      const { orderId, testId, file, ...opts } = args;
      return labApi.uploadReport(orderId, testId, file, opts);
    },
    onSuccess: ({ order }) => seedOrderCaches(qc, order),
  });
}

/**
 * Fetch a report's presigned URL.
 *
 * A mutation rather than a query on purpose. It has a side effect — the server
 * writes a `phi_access_log` row — so it must fire when a person asks for it, not
 * when a component happens to mount or React Query decides to refetch. Caching
 * it would also outlive the 120s URL.
 */
export function useLabReportUrl() {
  return useMutation({
    mutationFn: (documentId: string) => labApi.reportUrl(documentId),
  });
}

export function useCreateCatalogueEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: labApi.createCatalogueEntry,
    onSuccess: () => qc.invalidateQueries({ queryKey: [...LAB_KEY, 'catalogue'] }),
  });
}

export function useUpdateCatalogueEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; input: Parameters<typeof labApi.updateCatalogueEntry>[1] }) =>
      labApi.updateCatalogueEntry(args.id, args.input),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...LAB_KEY, 'catalogue'] }),
  });
}

/**
 * The worklist is invalidated alongside the list itself: its rows render the
 * laboratory NAME, which is resolved rather than snapshotted, so a rename that
 * did not reach them would leave two spellings on screen at once.
 */
function invalidateLaboratories(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: [...LAB_KEY, 'laboratories'] });
  void qc.invalidateQueries({ queryKey: [...LAB_KEY, 'worklist'] });
}

export function useCreateLaboratory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: labApi.createLaboratory,
    onSuccess: () => invalidateLaboratories(qc),
  });
}

export function useUpdateLaboratory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; input: Parameters<typeof labApi.updateLaboratory>[1] }) =>
      labApi.updateLaboratory(args.id, args.input),
    onSuccess: () => invalidateLaboratories(qc),
  });
}
